import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import {
  listDistricts,
  listRegions,
  listStates,
  listTerritories,
  listZones,
  type TerritoryRow,
} from '../../api/orgApi.js';

export function TerritoryTreePage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TerritoryRow[]>([]);
  const [zoneName, setZoneName] = useState<Record<string, string>>({});
  const [regionName, setRegionName] = useState<Record<string, string>>({});
  const [stateName, setStateName] = useState<Record<string, string>>({});
  const [districtName, setDistrictName] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterZone, setFilterZone] = useState('ALL');

  useEffect(() => {
    listTerritories(token)
      .then(r => setRows(r.data))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
    // The names fill the zone/region/state/district columns; unread, those
    // columns would read "—" as if the territory had none.
    const failed = (e: unknown) => setError((e as Error).message);
    listZones(token).then(r => setZoneName(Object.fromEntries(r.data.map(z => [z.zoneId, z.zoneName])))).catch(failed);
    listRegions(token).then(r => setRegionName(Object.fromEntries(r.data.map(x => [x.regionId, x.regionName])))).catch(failed);
    listStates(token).then(r => setStateName(Object.fromEntries(r.data.map(s => [s.stateId, s.stateName])))).catch(failed);
    listDistricts(token).then(r => setDistrictName(Object.fromEntries(r.data.map(d => [d.districtId, d.districtName])))).catch(failed);
  }, [token]);

  const zoneOf = (t: TerritoryRow): string => t.zoneName ?? (t.zoneId ? zoneName[t.zoneId] : undefined) ?? '—';
  const regionOf = (t: TerritoryRow): string => t.regionName ?? (t.regionId ? regionName[t.regionId] : undefined) ?? '—';
  const stateOf = (t: TerritoryRow): string => t.stateName ?? (t.stateId ? stateName[t.stateId] : undefined) ?? '—';
  const districtOf = (t: TerritoryRow): string => t.districtName ?? (t.districtId ? districtName[t.districtId] : undefined) ?? '—';

  const zones = useMemo(() => [...new Set(rows.map(zoneOf))].filter(z => z !== '—').sort(), [rows, zoneName]);
  const filtered = filterZone === 'ALL' ? rows : rows.filter(t => zoneOf(t) === filterZone);

  return (
    <main aria-label="territory-tree" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>{t('org.territory.tree.title')}</h1>
        <Link to="/org/territories/new" style={{ padding: '8px 16px', borderRadius: 6, background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', textDecoration: 'none' }}>{t('org.territory.tree.new')}</Link>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label htmlFor="zone-filter" style={{ marginRight: 8 }}>{t('org.territory.tree.zoneLabel')}</label>
        <select id="zone-filter" value={filterZone} onChange={e => setFilterZone(e.target.value)} style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          <option value="ALL">{t('org.territory.tree.allZones')}</option>
          {zones.map(z => <option key={z} value={z}>{z}</option>)}
        </select>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && filtered.length === 0 && <p>{t('org.territory.tree.empty')}</p>}
      {filtered.length > 0 && (
        <DataTable<TerritoryRow>
          rowKey={(row) => row.territoryId}
          rows={filtered}
          columns={[
            { key: 'code', header: t('org.col.code'), render: (row) => <span style={{ fontFamily: 'monospace' }}><Link to={`/org/territories/${row.territoryId}/edit`} style={{ color: 'var(--color-text-link)' }}>{row.territoryCode}</Link></span> },
            { key: 'territory', header: t('org.col.territory'), render: (row) => row.territoryName },
            { key: 'district', header: t('org.col.district'), render: (row) => districtOf(row) },
            { key: 'state', header: t('org.col.state'), render: (row) => stateOf(row) },
            { key: 'region', header: t('org.col.region'), render: (row) => regionOf(row) },
            { key: 'zone', header: t('org.col.zone'), render: (row) => zoneOf(row) },
          ]}
        />
      )}
    </main>
  );
}
